import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  ShoppingCart as SalesIcon,
  TrendingUp as ProfitIcon,
  Assessment as ReportsIcon,
  Download as ExportIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as DiscountIcon,
  TrendingDown as LossIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import LowStockDialog from './LowStockDialog';

// ── Stat Card ──
const StatCard = ({ icon: Icon, label, value, subtitle, color, onClick, clickable }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 3, p: { xs: 2, sm: 2.5 },
      background: '#2D2D2D',
      border: '1px solid rgba(255,255,255,0.08)',
      cursor: clickable ? 'pointer' : 'default',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': clickable ? {
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 28px rgba(229,57,53,0.12)',
      } : {
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      },
      '&::before': {
        content: '""', position: 'absolute',
        top: 0, right: 0, width: 80, height: 80,
        borderRadius: '50%', background: `${color}10`,
        transform: 'translate(20px, -20px)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box sx={{
        width: { xs: 28, sm: 34 }, height: { xs: 28, sm: 34 },
        borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}14`,
      }}>
        <Icon sx={{ fontSize: { xs: 16, sm: 18 }, color: color }} />
      </Box>
      <Typography sx={{
        fontSize: { xs: '0.62rem', sm: '0.72rem' },
        fontWeight: 600, color: '#BDBDBD',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontWeight: 800, color: color || '#F5F5F5',
      fontSize: { xs: '1rem', sm: '1.35rem' },
      letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.5,
      overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: { xs: '0.58rem', sm: '0.68rem' }, color: '#BDBDBD', fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ── Action Badge Color ──
const getActionStyle = (action) => {
  switch (action) {
    case 'SOLD': return { bg: 'rgba(16,185,129,0.1)', color: '#059669' };
    case 'CREATED': return { bg: 'rgba(14,165,233,0.1)', color: '#0284c7' };
    case 'BULK_CREATED': return { bg: 'rgba(99,102,241,0.1)', color: '#6366f1' };
    case 'UPDATED': return { bg: 'rgba(245,158,11,0.1)', color: '#d97706' };
    case 'DELETED': return { bg: 'rgba(239,68,68,0.1)', color: '#dc2626' };
    default: return { bg: 'rgba(255,255,255,0.05)', color: '#BDBDBD' };
  }
};

const ReportsDashboard = ({ shopName }) => {
  const [stats, setStats] = useState({
    totalItems: 0, totalValue: 0, potentialSalesValue: 0,
    totalSales: 0, estimatedProfit: 0, totalDiscount: 0,
    totalLoss: 0, totalCosts: 0, discountItems: [],
    lowStockItems: 0, lowStockItemsList: [], recentActivities: []
  });
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [filterAction, setFilterAction] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lowStockDialogOpen, setLowStockDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountBreakdown, setDiscountBreakdown] = useState([]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let items = [];
      let lowStockItems = [];
      let allActivities = [];

      try {
        items = await inventoryService.getAllItems(shopName);
        const threshold = 5;
        lowStockItems = items.filter(item => {
          const qty = Number(item.quantity) || 0;
          return qty > 0 && qty <= threshold;
        });
      } catch (err) { console.error('Error loading items:', err); }

      try {
        allActivities = await inventoryService.getAllActivities(shopName);
      } catch (err) { console.error('Error loading activities:', err); }

      const now = new Date();
      let startDate = new Date();

      switch (timePeriod) {
        case 'daily':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'weekly':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarterly':
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
          break;
        case 'annually':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      const filteredByPeriod = allActivities.filter(a => {
        const activityDate = new Date(a.timestamp);
        return activityDate.getTime() >= startDate.getTime();
      });

      const recentActivities = filteredByPeriod.slice(0, 20);

      const totalValue = items.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const cost = Number(item.originalCost) || 0;
        return sum + (qty * cost);
      }, 0);

      const potentialSalesValue = items.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.sellingPrice) || 0;
        return sum + (qty * price);
      }, 0);

      const soldActivities = filteredByPeriod.filter(a => a.action === 'SOLD');
      let totalSales = 0;
      let totalDiscount = 0;
      let totalCosts = 0;
      let totalLoss = 0;
      let netProfit = 0;
      const discountItems = [];
      const itemDiscountMap = new Map();

      soldActivities.forEach(activity => {
        let quantity = 0;
        const quantityMatch = activity.details?.match(/Sold\s+(\d+)\s+units/);
        if (quantityMatch) { quantity = parseFloat(quantityMatch[1]) || 0; }

        let discount = 0;
        if (activity.details) {
          let discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)\s+total/i);
          if (discountMatch && discountMatch[1]) {
            discount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
          } else {
            discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)/i);
            if (discountMatch && discountMatch[1]) {
              discount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
            }
          }
        }

        if (discount === 0 && activity.discountAmount !== undefined && activity.discountAmount !== null) {
          const discountValue = activity.discountAmount;
          discount = typeof discountValue === 'number' ? discountValue : parseFloat(discountValue) || 0;
        }

        let finalSaleAmount = 0;
        const finalMatch = activity.details?.match(/Final:\s*₱([\d,]+\.?\d*)/i);
        if (finalMatch && finalMatch[1]) {
          finalSaleAmount = parseFloat(finalMatch[1].replace(/,/g, '')) || 0;
        } else {
          const amountMatch = activity.details?.match(/₱([\d,]+\.?\d*)/);
          if (amountMatch) {
            let amountStr = amountMatch[1].replace(/,/g, '');
            if (amountStr.includes(' ')) { amountStr = amountStr.split(' ')[0]; }
            finalSaleAmount = parseFloat(amountStr) || 0;
          }
        }

        const originalSaleAmount = discount > 0 ? finalSaleAmount + discount : finalSaleAmount;

        if (!isNaN(finalSaleAmount) && finalSaleAmount > 0) { totalSales += finalSaleAmount; }
        totalDiscount += discount;

        let itemOriginalCost = parseFloat(activity.itemCost) || 0;
        if (itemOriginalCost === 0) {
          let item = items.find(i => i.name === activity.itemName && i.brand === activity.itemBrand);
          if (!item) { item = items.find(i => i.name === activity.itemName); }
          itemOriginalCost = item ? (parseFloat(item.originalCost) || 0) : 0;
        }

        const costOfGoodsSold = quantity * itemOriginalCost;
        totalCosts += costOfGoodsSold;

        const transactionLoss = Math.max(0, costOfGoodsSold - finalSaleAmount);
        totalLoss += transactionLoss;

        if (discount > 0) {
          discountItems.push({
            itemName: activity.itemName,
            itemBrand: activity.itemBrand,
            quantity: quantity,
            saleAmount: originalSaleAmount,
            discount: discount,
            cost: costOfGoodsSold,
            loss: transactionLoss,
            date: activity.timestamp
          });

          const key = `${activity.itemName}|${activity.itemBrand}`;
          if (itemDiscountMap.has(key)) {
            const existing = itemDiscountMap.get(key);
            existing.quantity += quantity;
            existing.saleAmount += originalSaleAmount;
            existing.discount += discount;
            existing.cost += costOfGoodsSold;
            existing.loss += transactionLoss;
          } else {
            itemDiscountMap.set(key, {
              itemName: activity.itemName,
              itemBrand: activity.itemBrand,
              quantity: quantity,
              saleAmount: originalSaleAmount,
              discount: discount,
              cost: costOfGoodsSold,
              loss: transactionLoss,
              date: activity.timestamp
            });
          }
        }
      });

      discountItems.sort((a, b) => b.date - a.date);
      netProfit = totalSales - totalCosts;

      setStats({
        totalItems: items.length, totalValue, potentialSalesValue,
        totalSales, estimatedProfit: netProfit,
        totalDiscount, totalLoss, totalCosts,
        discountItems,
        lowStockItems: lowStockItems.length,
        lowStockItemsList: lowStockItems,
        recentActivities: recentActivities.slice(0, 5)
      });

      setActivities(filteredByPeriod);
      setFilteredActivities(filteredByPeriod);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [shopName, timePeriod]);

  const filterActivities = useCallback(() => {
    let filtered = activities;
    if (filterAction) { filtered = filtered.filter(activity => activity.action === filterAction); }
    if (dateRange.start && dateRange.end) {
      const startTime = new Date(dateRange.start).getTime();
      const endTime = new Date(dateRange.end).getTime();
      filtered = filtered.filter(activity => activity.timestamp >= startTime && activity.timestamp <= endTime);
    }
    setFilteredActivities(filtered);
  }, [activities, filterAction, dateRange]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);
  useEffect(() => { filterActivities(); }, [filterActivities]);


  const formatCurrency = (amount) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);

  const formatDate = (timestamp) => new Date(timestamp).toLocaleDateString('en-PH', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const getPeriodLabel = () => {
    switch (timePeriod) {
      case 'daily': return 'Today';
      case 'weekly': return 'Last 7 Days';
      case 'monthly': return 'This Month';
      case 'quarterly': return 'This Quarter';
      case 'annually': return 'This Year';
      default: return 'This Month';
    }
  };

  const exportToCSV = () => {
    if (filteredActivities.length === 0) { alert('No activities to export'); return; }
    const csvData = filteredActivities.map(activity => ({
      Date: formatDate(activity.timestamp),
      Action: activity.action,
      Item: activity.itemName || '',
      Brand: activity.itemBrand || '',
      Category: activity.itemCategory || '',
      Details: activity.details || ''
    }));
    const csvString = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${shopName}_activities_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <ReportsIcon sx={{ fontSize: 28, color: '#F5F5F5' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#F5F5F5' }}>Loading reports...</Typography>
        </Box>
        <LinearProgress sx={{ borderRadius: 2, height: 4 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => loadDashboardData()}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 3, sm: 4 },
        flexDirection: { xs: 'column', sm: 'row' }, gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5,
            background: '#E53935',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(229,57,53,0.3)',
          }}>
            <ReportsIcon sx={{ fontSize: 22, color: 'white' }} />
          </Box>
          <Box>
            <Typography sx={{
              fontWeight: 800, color: '#F5F5F5',
              fontSize: { xs: '1.15rem', sm: '1.5rem' },
              letterSpacing: '-0.02em',
            }}>
              Reports
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD', fontWeight: 500 }}>
              {shopName} • {getPeriodLabel()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={timePeriod} label="Time Period" onChange={(e) => setTimePeriod(e.target.value)}>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annually">Annually</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={() => loadDashboardData()}
            disabled={loading}
            size="small"
            sx={{
              borderColor: 'rgba(229,57,53,0.2)', color: '#E53935',
              '&:hover': { borderColor: '#E53935', background: 'rgba(229,57,53,0.04)' },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: { xs: 1.5, sm: 2 },
        mb: { xs: 3, sm: 4 },
      }}>
        <StatCard icon={InventoryIcon} label="Total Items" value={stats.totalItems} color="#E53935" />
        <StatCard icon={SalesIcon} label="Total Sales" value={formatCurrency(stats.totalSales)} color="#10b981" />
        <StatCard icon={MoneyIcon} label="Total Costs" value={formatCurrency(stats.totalCosts)} color="#6366f1" />
        <StatCard icon={WarningIcon} label="Low Stock" value={stats.lowStockItems}
          subtitle={stats.lowStockItems > 0 ? 'Click to view & edit' : 'All stocked'}
          color="#f59e0b" clickable={stats.lowStockItems > 0}
          onClick={() => stats.lowStockItems > 0 && setLowStockDialogOpen(true)} />
        <StatCard icon={ProfitIcon} label="Net Profit" value={formatCurrency(stats.estimatedProfit)}
          subtitle="Sales − Costs"
          color={stats.estimatedProfit >= 0 ? '#10b981' : '#ef4444'} />
        <StatCard icon={DiscountIcon} label="Discount" value={formatCurrency(stats.totalDiscount)}
          subtitle={stats.discountItems?.length > 0 ? 'Click for breakdown' : undefined}
          color="#f59e0b" clickable={stats.discountItems?.length > 0}
          onClick={() => { setDiscountBreakdown(stats.discountItems || []); setDiscountDialogOpen(true); }} />
        <StatCard icon={LossIcon} label="Total Loss" value={formatCurrency(stats.totalLoss)}
          subtitle={stats.discountItems?.length > 0 ? 'Click for breakdown' : undefined}
          color="#ef4444" clickable={stats.discountItems?.length > 0}
          onClick={() => { setDiscountBreakdown(stats.discountItems || []); setDiscountDialogOpen(true); }} />
        <StatCard icon={InventoryIcon} label="Inventory Value" value={formatCurrency(stats.totalValue)} color="#E53935" />
      </Box>

      {/* ── Activity Panels ── */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            borderRadius: 3, p: { xs: 2, sm: 3 },
            background: '#2D2D2D', border: '1px solid rgba(255,255,255,0.08)',
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.3)' },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ fontSize: 20, color: '#E53935' }} />
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#F5F5F5' }}>
                Recent Activities
              </Typography>
            </Box>
            <List disablePadding>
              {stats.recentActivities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HistoryIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>No recent activities</Typography>
                </Box>
              ) : (
                stats.recentActivities.map((activity, index) => {
                  const style = getActionStyle(activity.action);
                  return (
                    <React.Fragment key={activity.id || `activity-${index}`}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                label={activity.action}
                                size="small"
                                sx={{
                                  fontWeight: 700, fontSize: '0.6rem', height: 22,
                                  background: style.bg, color: style.color,
                                  border: 'none',
                                }}
                              />
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#F5F5F5' }}>
                                {activity.itemName}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD' }}>
                                {activity.details}
                              </Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: '#BDBDBD', mt: 0.3 }}>
                                {formatDate(activity.timestamp)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < stats.recentActivities.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />}
                    </React.Fragment>
                  );
                })
              )}
            </List>
          </Box>
        </Grid>

        {/* Activity Log */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            borderRadius: 3, p: { xs: 2, sm: 3 },
            background: '#2D2D2D', border: '1px solid rgba(255,255,255,0.08)',
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.3)' },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon sx={{ fontSize: 20, color: '#E53935' }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#F5F5F5' }}>
                  Activity Log
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportToCSV}
                size="small"
                sx={{
                  fontSize: '0.72rem', fontWeight: 600,
                  borderColor: 'rgba(229,57,53,0.2)', color: '#E53935',
                  '&:hover': { borderColor: '#E53935', background: 'rgba(229,57,53,0.04)' },
                }}
              >
                Export
              </Button>
            </Box>

            {/* Filters */}
            <Box sx={{
              display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap',
              p: 1.5, borderRadius: 2,
              background: 'rgba(229,57,53,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <FormControl size="small" sx={{ minWidth: 110, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                <InputLabel>Action</InputLabel>
                <Select value={filterAction} label="Action" onChange={(e) => setFilterAction(e.target.value)}>
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="SOLD">Sold</MenuItem>
                  <MenuItem value="CREATED">Created</MenuItem>
                  <MenuItem value="BULK_CREATED">Bulk Created</MenuItem>
                  <MenuItem value="UPDATED">Updated</MenuItem>
                  <MenuItem value="DELETED">Deleted</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Start Date" type="date" size="small"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
              />
              <TextField
                label="End Date" type="date" size="small"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
              />
            </Box>

            <List sx={{ maxHeight: 400, overflow: 'auto' }} disablePadding>
              {filteredActivities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <FilterIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    {activities.length === 0 ? 'No activities found.' : 'No activities for these filters.'}
                  </Typography>
                </Box>
              ) : (
                filteredActivities.map((activity, index) => {
                  const style = getActionStyle(activity.action);
                  return (
                    <React.Fragment key={activity.id || `filtered-activity-${index}`}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                              <Chip
                                label={activity.action}
                                size="small"
                                sx={{
                                  fontWeight: 700, fontSize: '0.6rem', height: 22,
                                  background: style.bg, color: style.color,
                                  border: 'none',
                                }}
                              />
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#F5F5F5' }}>
                                {activity.itemName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: '#BDBDBD' }}>
                                ({activity.itemBrand})
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD' }}>
                                {activity.details}
                              </Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: '#BDBDBD', mt: 0.3 }}>
                                {formatDate(activity.timestamp)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < filteredActivities.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />}
                    </React.Fragment>
                  );
                })
              )}
            </List>
          </Box>
        </Grid>
      </Grid>

      {/* Low Stock Dialog */}
      <LowStockDialog
        open={lowStockDialogOpen}
        onClose={() => setLowStockDialogOpen(false)}
        shopName={shopName}
        onUpdate={loadDashboardData}
      />

      {/* Discount/Loss Breakdown Dialog */}
      <Dialog
        open={discountDialogOpen}
        onClose={() => setDiscountDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          background: '#E53935',
          color: 'white', pb: 2,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 38, height: 38, borderRadius: 2,
                background: 'rgba(245,158,11,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <DiscountIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Discount & Loss Breakdown</Typography>
            </Box>
            <Chip
              label={`${discountBreakdown.length} transaction${discountBreakdown.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontWeight: 700, fontSize: '0.68rem',
                background: 'rgba(245,158,11,0.2)', color: '#fbbf24',
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {discountBreakdown.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <DiscountIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
              <Typography sx={{ color: '#BDBDBD' }}>No discounts applied to any sales.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', fontSize: '0.75rem' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', fontSize: '0.75rem' }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', fontSize: '0.75rem' }}>Brand</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', fontSize: '0.75rem' }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', fontSize: '0.75rem' }}>Sale Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', color: '#d97706', fontSize: '0.75rem' }}>Discount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#3A3A3A', color: '#dc2626', fontSize: '0.75rem' }}>Loss</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {discountBreakdown.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(item.date)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{item.itemName}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{item.itemBrand}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{item.quantity}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(item.saleAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 700 }}>
                        {formatCurrency(item.discount || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>
                        {formatCurrency(item.loss || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ background: 'rgba(229,57,53,0.04)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#F5F5F5' }}>
                      Total:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#d97706', fontSize: '0.85rem' }}>
                      {formatCurrency(discountBreakdown.reduce((sum, item) => sum + (item.discount || 0), 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '0.85rem' }}>
                      {formatCurrency(discountBreakdown.reduce((sum, item) => sum + (item.loss || 0), 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDiscountDialogOpen(false)} sx={{ fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsDashboard;